export const COOKIEYES_CATEGORIES = {
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
  ADVERTISEMENT: 'advertisement',
  PERFORMANCE: 'performance',
} as const;

export const COOKIEYES_CONSENT_CHANGED_EVENT = 'cookieyes-consent-changed';

type CookieYesCategories = {
  necessary?: boolean;
  functional?: boolean;
  analytics?: boolean;
  advertisement?: boolean;
  performance?: boolean;
  other?: boolean;
};

type CookieYesConsent = {
  activeLaw?: string;
  categories?: CookieYesCategories;
  isUserActionCompleted?: boolean;
  consentID?: string;
  languageCode?: string;
};

function getCkyConsentSafe(): CookieYesConsent | null {
  if (typeof window.getCkyConsent !== 'function') return null;
  try {
    return window.getCkyConsent() || null;
  } catch {
    return null;
  }
}

/** Parse cookieyes-consent cookie as fallback when getCkyConsent() is late/empty. */
function getConsentFromCookie(): {
  actionCompleted: boolean;
  categories: CookieYesCategories;
} | null {
  const match = document.cookie.match(/(?:^|;\s*)cookieyes-consent=([^;]*)/);
  if (!match) return null;

  try {
    const raw = decodeURIComponent(match[1]);
    const parts: Record<string, string> = {};
    raw.split(',').forEach((pair) => {
      const [key, value] = pair.split(':');
      if (key) parts[key.trim()] = (value || '').trim();
    });

    return {
      // CookieYes sets action:yes after Accept/Reject/Save
      actionCompleted: parts.action === 'yes' || parts.consent === 'yes',
      categories: {
        necessary: parts.necessary === 'yes',
        functional: parts.functional === 'yes',
        analytics: parts.analytics === 'yes',
        advertisement: parts.advertisement === 'yes',
        performance: parts.performance === 'yes',
      },
    };
  } catch {
    return null;
  }
}

export function hasUserMadeConsentChoice(): boolean {
  const consent = getCkyConsentSafe();
  if (consent?.isUserActionCompleted) return true;
  return !!getConsentFromCookie()?.actionCompleted;
}

export function hasConsent(category: string): boolean {
  if (!hasUserMadeConsentChoice()) return false;

  const consent = getCkyConsentSafe();
  const fromApi = consent?.categories as
    | Record<string, boolean | undefined>
    | undefined;
  if (fromApi && typeof fromApi[category] === 'boolean') {
    return !!fromApi[category];
  }

  const fromCookie = getConsentFromCookie()?.categories as
    | Record<string, boolean | undefined>
    | undefined;
  return !!fromCookie?.[category];
}

export function hasAnalyticsConsent(): boolean {
  return hasConsent(COOKIEYES_CATEGORIES.ANALYTICS);
}

export function hasTargetingConsent(): boolean {
  return hasConsent(COOKIEYES_CATEGORIES.ADVERTISEMENT);
}

export function hasFunctionalConsent(): boolean {
  return hasConsent(COOKIEYES_CATEGORIES.FUNCTIONAL);
}

export function getConsentDebugSnapshot() {
  return {
    api: getCkyConsentSafe(),
    cookie: getConsentFromCookie(),
    hasUserMadeConsentChoice: hasUserMadeConsentChoice(),
    analytics: hasAnalyticsConsent(),
    advertisement: hasTargetingConsent(),
    functional: hasFunctionalConsent(),
  };
}

export function subscribeToConsentChanges(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener(COOKIEYES_CONSENT_CHANGED_EVENT, handler);

  const onBannerLoad = () => callback();
  const onConsentUpdate = () => callback();
  document.addEventListener('cookieyes_banner_load', onBannerLoad);
  document.addEventListener('cookieyes_consent_update', onConsentUpdate);

  return () => {
    window.removeEventListener(COOKIEYES_CONSENT_CHANGED_EVENT, handler);
    document.removeEventListener('cookieyes_banner_load', onBannerLoad);
    document.removeEventListener('cookieyes_consent_update', onConsentUpdate);
  };
}
